import {
  AdminDocument,
  BankAccountInfoDocument,
  ExemptRegions,
  PersonalDetail
} from 'common'
import {ExemptionType, applicationStatus} from 'common/enums'
import {IAppContext} from 'components/Context'
import {UserInformation} from 'components/types'
import {FirebaseInstances} from './helpers'
import {
  adminsApi,
  personalDetailsApi,
  bankDetailsApi,
  financeApi,
  taxExemptionsApi
} from 'lib/api'

const getApiErrorMessage = (error: any, fallbackMessage: string) => {
  const details = error?.details

  if (Array.isArray(details) && details.length) {
    return details.map(String).join('\n')
  }

  const message =
    error?.response?.data?.msg ||
    error?.response?.data?.data ||
    error?.body?.msg ||
    error?.body?.data ||
    error?.message

  return typeof message === 'string' && message.trim()
    ? message
    : fallbackMessage
}

const getApiSecurityErrorCode = (error: any) => {
  const errorCode = error?.body?.errorCode
  return typeof errorCode === 'string' ? errorCode : undefined
}

export function createAdminService(_fb: FirebaseInstances) {
  const createAdmin = async (adminData: UserInformation) => {
    try {
      const result = await adminsApi.create<{msg: string}>(adminData)
      return {
        response: result,
        message: (result as any)?.msg || 'Admin created'
      }
    } catch (err: any) {
      const {response} = err
      const {status} = response || {}
      return {
        error: getApiErrorMessage(err, 'Unable to create admin user.'),
        errorCode: status,
        securityErrorCode: getApiSecurityErrorCode(err)
      }
    }
  }

  const editAdmin = async (adminData: UserInformation, adminID: string) => {
    try {
      const result = await adminsApi.update<{msg: string}>(adminID, adminData)
      return {
        response: result,
        message: (result as any)?.msg || 'Admin updated'
      }
    } catch (error: any) {
      const {response} = error
      const {status} = response || {}
      return {
        error: getApiErrorMessage(error, 'Unable to update admin user.'),
        errorCode: status,
        securityErrorCode: getApiSecurityErrorCode(error)
      }
    }
  }

  const deleteAdmin = async (adminID: string) => {
    try {
      await adminsApi.delete(adminID)
      return {message: 'Admin deleted successfully'}
    } catch (error: any) {
      const {response} = error
      const {data, status} = response || {}
      return {error: data?.msg || data?.data || error, errorCode: status}
    }
  }

  const grantDenyAdminAccess = async (adminID: string, isGrant: boolean) => {
    try {
      const result = await adminsApi.updateStatus<{msg: string}>(
        adminID,
        isGrant
      )
      return {
        response: result,
        message:
          (result as any)?.msg ||
          `Admin access ${isGrant ? 'granted' : 'denied'}`
      }
    } catch (error: any) {
      const {response} = error
      const {data, status} = response || {}
      return {error: data?.msg || data?.data || error, errorCode: status}
    }
  }

  const waiveBidderDeposit = async (userId: string, status: boolean) => {
    try {
      await personalDetailsApi.waiveBidderDeposit(userId, status)
      return {status: true}
    } catch (error) {
      return {status: false}
    }
  }

  const getUserPersonalDetail = ({
    userId,
    cancelCallback,
    updateState
  }: {
    userId: string
    updateState: (e: any) => void
    cancelCallback: (e: Error) => void
  }) => {
    const fetchDetail = async () => {
      try {
        const result = await personalDetailsApi.get<{data: PersonalDetail}>(
          userId
        )
        const detail = (result as any)?.data || result
        updateState(detail as PersonalDetail)
      } catch (e) {
        cancelCallback(e as Error)
      }
    }
    fetchDetail()
    return {unSubscribe: () => {}}
  }

  const updateUserPersonalDetail = async (
    userId: string,
    payload: Partial<PersonalDetail> & {reportBccEmails?: Array<string>}
  ) => {
    try {
      const result = await personalDetailsApi.update<{data: PersonalDetail}>(
        userId,
        payload
      )
      return {
        status: true,
        data: ((result as any)?.data || result) as PersonalDetail
      }
    } catch (error: any) {
      return {
        status: false,
        message:
          error?.response?.data?.msg ||
          error?.response?.data?.data ||
          'Something went wrong! Failed to update personal details.'
      }
    }
  }

  const getUserBankAccountInfo = async (accountId: string, userId?: string) => {
    try {
      if (!userId) return
      const result = await bankDetailsApi.get<{data: BankAccountInfoDocument}>(
        userId,
        accountId
      )
      return {
        userBankData: ((result as any)?.userBankData ||
          result) as BankAccountInfoDocument
      }
    } catch (error) {
      return {error}
    }
  }

  const updateAdminBankAccountInfo = async (info: BankAccountInfoDocument) => {
    try {
      await bankDetailsApi.updateAdmin(info)
      return {}
    } catch (error) {
      return {error}
    }
  }

  const getAdminDocument = (
    updateState: React.Dispatch<React.SetStateAction<IAppContext['appState']>>
  ) => {
    const fetchAdmin = async () => {
      try {
        const result = await adminsApi.getCurrent<{data: AdminDocument}>()
        const adminDoc = (result as any)?.data || result
        updateState((prev) => ({
          ...prev,
          adminDetails: adminDoc as AdminDocument
        }))
      } catch (error) {
        console.error(
          'Something went wrong! Failed to get admin details',
          error
        )
      }
    }
    fetchAdmin()
    return {unsubscribe: () => {}}
  }

  const updateFinanceStatus = async (
    userId: string,
    financeBody: {financeId: string; financingStatus: applicationStatus}
  ) => {
    try {
      const result = await financeApi.updateStatus<{msg: string}>(
        userId,
        financeBody.financeId,
        financeBody.financingStatus
      )
      return {response: result, message: (result as any)?.msg, success: true}
    } catch (err: any) {
      return {
        error: err?.response?.data?.msg || err,
        errorCode: err?.response?.status
      }
    }
  }

  const updateExemptionTypeAndRegions = async ({
    taxExemptionId,
    accountId,
    exemptRegions,
    exemptionType
  }: {
    taxExemptionId: string
    accountId?: string
    exemptionType?: ExemptionType
    exemptRegions?: Array<ExemptRegions>
  }) => {
    try {
      await taxExemptionsApi.updateTypeAndRegions(taxExemptionId, {
        accountId,
        exemptRegions,
        exemptionType
      })
      return {
        success: true,
        message: 'Tax exemption type and regions updated successfully.'
      }
    } catch (error: any) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Something went wrong! Failed to update exemption type and regions.'
        )
      }
    }
  }

  const updateExemptionCertificate = async ({
    taxExemptionId,
    accountId,
    status,
    certificateId
  }: {
    taxExemptionId: string
    accountId: string
    certificateId: string
    status: applicationStatus
  }) => {
    try {
      await taxExemptionsApi.updateCertificate(taxExemptionId, {
        accountId,
        status,
        certificateId
      })
      return {
        success: true,
        message: 'Tax exemption certificate updated successfully.'
      }
    } catch (error) {
      return {
        success: false,
        message: 'Something went wrong! Failed to update exemption certificate.'
      }
    }
  }

  return {
    createAdmin,
    editAdmin,
    deleteAdmin,
    grantDenyAdminAccess,
    waiveBidderDeposit,
    getUserPersonalDetail,
    getUserBankAccountInfo,
    updateUserPersonalDetail,
    updateAdminBankAccountInfo,
    getAdminDocument,
    updateFinanceStatus,
    updateExemptionTypeAndRegions,
    updateExemptionCertificate
  }
}
